import { useOfferStore as useStore } from '@/stores/OfferStore'
import { useRouter } from 'vue-router'
import { Offer } from '@/model/types'
import { makeABackendCall } from '@/BaseViewFunctionality'
import { storeToRefs } from 'pinia'
import { ref } from 'vue'

export default function useOfferStore() {
    const offerStore = useStore()
    const router = useRouter()
    const { selectedOffer, proposedOffer, contracts, signaturesList, offers, loading } =
        storeToRefs(offerStore)
    const searchTerm = ref<string>('')

    // Available HATEOAS links
    const availableLinks = ref<any[]>([])

    const handleError = (error: any, message = 'An error occurred') => {
        console.error(message, error)
    }

    const selectOffer = async (offer: Offer, contactId: number) => {
        try {
            await offerStore.fetchContracts()
            if (Array.isArray(contracts.value)) {
                offerStore.selectOffer(offer, contactId, contracts.value)
            } else {
                console.error('Contracts data is not available')
            }
        } catch (error) {
            handleError(error, 'Error selecting offer')
        }
    }

    const confirmDeleteSingleOffer = async (contactId: number, offerId: number) => {
        try {
            const confirmed = window.confirm('Are you sure you want to delete this offer?')
            if (confirmed) {
                await offerStore.deleteOffer({ contactId, offerId })
            }
        } catch (error) {
            handleError(error, 'Error confirming delete of single offer')
        }
    }

    const downloadPdf = async (offerId: number) => {
        try {
            await offerStore.downloadPdf({ offerId })
        } catch (error) {
            handleError(error, 'Error downloading PDF')
        }
    }

    const fetchContracts = async () => {
        try {
            await offerStore.fetchContracts()
            return contracts.value
        } catch (error) {
            handleError(error, 'Error fetching contracts')
            return []
        }
    }

    const clearSelectedOfferStore = () => {
        try {
            offerStore.clearSelectedOfferStore()
        } catch (error) {
            handleError(error, 'Error clearing selected offer store')
        }
    }

    const handleAsyncOperation = async (operation: () => Promise<any>, params: any = {}) => {
        try {
            return await makeABackendCall({
                promise: operation(),
                parameters: params,
                loading: loading.value,
            })
        } catch (error) {
            handleError(error)
            return null
        }
    }

    const generatePdf = async ({
        proposedOffer,
        offerId,
        contactId,
    }: {
        proposedOffer: any
        offerId: number
        contactId: number
    }) => {
        try {
            await offerStore.generatePdf({ proposedOffer, offerId, contactId })
            router.push(`/offers/schoolview/${contactId}`)
        } catch (error) {
            handleError(error, 'Error generating PDF')
        }
    }

    const fetchSignaturesList = async () => {
        try {
            await offerStore.fetchSignaturesList()
            return signaturesList.value
        } catch (error) {
            handleError(error, 'Error fetching signatures list')
            return []
        }
    }

    const updateOffer = async ({
        contactId,
        offerId,
        offer,
    }: {
        contactId: number
        offerId: number
        offer: any
    }): Promise<boolean> => {
        try {
            await offerStore.updateOffer({ contactId, offerId, offer })
            return true
        } catch (error) {
            handleError(error, 'Error updating offer')
            return false
        }
    }

    const loadOffers = ({ done }: { done: () => void }) => {
        handleAsyncOperation(() =>
            offerStore.fetchOffersLimit({
                limit: 20,
                offset: offers.value.length,
                filter: searchTerm.value,
                done,
            })
        )
    }

    const createOffer = async ({
        contactId,
        offer,
    }: {
        contactId: number
        offer: any
    }): Promise<any> => {
        try {
            const result = await offerStore.createOffer({ contactId, offer })
            return result
        } catch (error) {
            handleError(error, 'Error creating offer')
            return null
        }
    }

    const deleteOffers = async (contactId: number) => {
        try {
            await makeABackendCall({
                promise: offerStore.deleteOffers,
                parameters: { contactId },
                loading: loading.value,
                confirmMessage: 'Sind sie sicher?',
            })
            return true
        } catch (error) {
            handleError(error, 'Error deleting offers')
            return false
        }
    }

    const fetchOffers = async () => {
        try {
            return await makeABackendCall({
                promise: offerStore.fetchOffers,
                loading: loading.value,
            })
        } catch (error) {
            handleError(error, 'Error fetching offers')
            return null
        }
    }

    const search = async () => {
        try {
            offerStore.resetOffers()
            await makeABackendCall({
                promise: offerStore.fetchOffersSearch,
                parameters: { limit: 20, offset: 0, filter: searchTerm.value },
                loading: loading.value,
            })
        } catch (error) {
            handleError(error, 'Error performing search')
        }
    }

    const fetchOfferAccess = async (offerId: number) => {
        try {
            loading.value = true
            console.log(`Fetching access for offer ID: ${offerId}`)
            const response = await offerStore.fetchOfferAccess({ offerId })
            console.log('Raw API response:', response)
            if (!response) {
                console.warn('Received null/undefined response')
                return { roles: [] }
            }
            if (!response.roles && response.data && response.data.roles) {
                console.log('Restructuring response to match expected format')
                return { roles: response.data.roles }
            }
            if (!response.roles) {
                console.warn('No roles property in response')
                return { roles: [] }
            }
            return response
        } catch (error) {
            handleError(error, 'Error fetching offer access')
            return { roles: [] }
        } finally {
            loading.value = false
        }
    }

    const updateOfferAccess = async (offerId: number, roleIds: number[]): Promise<boolean> => {
        try {
            console.log('Updating offer access - offer ID:', offerId, 'role IDs:', roleIds)
            const validRoleIds = Array.isArray(roleIds) ? roleIds : []
            if (!validRoleIds.includes(33)) {
                console.log('Note: Anykey role (33) is being removed from access')
            }
            await offerStore.updateOfferAccess(offerId, validRoleIds)
            return true
        } catch (error) {
            handleError(error, 'Error updating offer access')
            return false
        }
    }

    const getHateoasLinks = async () => {
        try {
            loading.value = true
            const response = await offerStore.getHateoasLinks()
            console.log('HATEOAS API response:', response)
            availableLinks.value = response || []
            return availableLinks.value
        } catch (error) {
            handleError(error, 'Error fetching HATEOAS links')
            return []
        } finally {
            loading.value = false
        }
    }

    const hasLink = (rel: string) => {
        return availableLinks.value && availableLinks.value.some((link) => link.rel === rel)
    }

    const fetchThirdPartyPartners = async () => {
        try {
            loading.value = true
            const response = await offerStore.fetchThirdPartyPartners()
            console.log('Third parties', response)
            return response
        } catch (error) {
            handleError(error, 'Error fetching Third Parties')
            return []
        } finally {
            loading.value = false
        }
    }

    return {
        offers,
        loading,
        selectedOffer,
        proposedOffer,
        searchTerm,
        contracts,
        signaturesList,
        fetchOffers,
        loadOffers,
        deleteOffers,
        selectOffer,
        confirmDeleteSingleOffer,
        downloadPdf,
        fetchContracts,
        clearSelectedOfferStore,
        search,
        generatePdf,
        fetchSignaturesList,
        updateOffer,
        createOffer,
        fetchOfferAccess,
        updateOfferAccess,
        getHateoasLinks,
        availableLinks,
        hasLink,
        fetchThirdPartyPartners,
    }
}
